import { View } from 'react-native';
import { useSelector } from 'react-redux';
import { CheckBox, Radio, useTheme } from '@ui-kitten/components';
import { useFormikContext } from 'formik';
import lodashFilter from 'lodash/filter';
import lodashFind from 'lodash/find';
import lodashFindIndex from 'lodash/findIndex';
import lodashIsEmpty from 'lodash/isEmpty';

import Button from '../../Components/Button';
import ReadMore from '../../Components/ReadMore';
import {
  formikNames,
  textCategory,
} from '../../Components/Reservation/constants';
import { styles as reservationStyles } from '../../Components/Reservation/styles';
import ResponsiveText from '../../Components/ResponsiveText';
import { getCurrency } from '../../Helper/Routines';
import useAlert from '../../Hooks/useAlert';
import { ExtraGroupItem } from '../../RTK/api/types';
import { selectReservationCheckoutData } from '../../RTK/utility/selectors';
import themeStyle from '../../Theme/styles';

type AddOnsCardProps = ExtraGroupItem & {
  isError: boolean;
  isMultiple: boolean;
  multipleLimit: number;
  parentName: string;
};

const AddOnsCard = (addOn: AddOnsCardProps) => {
  const alert = useAlert();
  const theme = useTheme();
  const rcd = useSelector(selectReservationCheckoutData);
  const { values, setErrors, setFieldTouched, setFieldValue } =
    useFormikContext();

  const addOnValues = values[formikNames.ADDONS];
  const addOnCart = lodashFind(addOnValues, { id: addOn.id });
  const isAdded = !lodashIsEmpty(addOnCart);
  const isDisabled = !rcd?.enabledAddOns?.includes?.(addOn.id);

  const OptionComponent = (
    addOn.isMultiple ? CheckBox : Radio
  ) as React.ComponentType<any>;

  // TODO - put extra group logic on a custom hook and reuse it on item page extra group for DRY
  const _onPress = async () => {
    const copyAddOnValues = JSON.parse(JSON.stringify(addOnValues));
    let shouldSave = false;
    if (addOn.isMultiple) {
      // if multiple
      if (isAdded) {
        // if already added (remove currently selected)
        const valueIndex = lodashFindIndex(addOnValues, { id: addOn.id });
        copyAddOnValues.splice(valueIndex, 1);
        shouldSave = true;
      }
    } else {
      // if not multiple
      const otherSelectedExtraItem = lodashFindIndex(
        addOnValues,
        ({ id, parentName }) =>
          id !== addOn.id && parentName === addOn.parentName,
      );
      if (otherSelectedExtraItem !== -1) {
        // if has other selected, remove it
        copyAddOnValues.splice(otherSelectedExtraItem, 1);
        shouldSave = true;
      }
    }
    if (!isAdded) {
      // common behavior for single and multiple for saving selected
      const parentAddOnCount = lodashFilter(addOnValues, {
        parentName: addOn.parentName,
      }).length;
      if (addOn.isMultiple && parentAddOnCount >= addOn.multipleLimit) {
        // warning for multiple that they can only add up to multipleLimit
        alert(
          'Maximum Reached',
          `You can select up to ${addOn.multipleLimit} only`,
          { web: { buttonStatus: 'info' } },
        );
      } else {
        shouldSave = true;
        copyAddOnValues.push(addOn);
      }
    }
    if (shouldSave) {
      setErrors({ [formikNames.ADDONS]: undefined });
      setFieldTouched(formikNames.ADDONS);
      setFieldValue(formikNames.ADDONS, copyAddOnValues);
    }
  };

  return (
    <Button disabled={isDisabled} onPress={_onPress} plain>
      <View
        style={[
          reservationStyles.detailsBox,
          themeStyle.pageHorizontalSpacing,
          addOn.isError && { borderColor: theme['color-danger-500'] },
        ]}
      >
        <View style={[themeStyle.flex1, themeStyle.flexDirectionRow]}>
          <View style={[themeStyle.flex1, themeStyle.spacingBottomXSmall]}>
            <ResponsiveText
              category={textCategory.TITLE}
              style={[themeStyle.flex1, themeStyle.bold]}
            >
              {addOn.name}
            </ResponsiveText>
            {Boolean(addOn.description) && (
              <ReadMore
                category={textCategory.BODY}
                contentContainerStyle={themeStyle.spacingBottomXSmall}
                maxLine={2}
                status="info"
                value={addOn.description}
              />
            )}
            <ResponsiveText
              category={textCategory.BODY}
              style={themeStyle.bold}
            >
              {getCurrency(Number(addOn.price))}
            </ResponsiveText>
          </View>
          <View>
            <OptionComponent
              status={addOn.isError ? 'danger' : isAdded ? 'info' : 'basic'}
              checked={isAdded}
              onChange={_onPress}
            />
          </View>
        </View>
      </View>
    </Button>
  );
};

export default AddOnsCard;
